import { Button, Flex, Popover, Text, TextField } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";
import {
	type CollaborationRoomListing,
	createRoom,
	listPublicRooms,
	makeRoomKey,
} from "$/modules/collaboration/protocol.ts";
import { useCollaboration } from "$/modules/collaboration/useCollaboration.ts";

const params = new URLSearchParams(window.location.search);
const sessionStorageKey = "amll-collaboration-session";
const savedSession = (() => {
	try {
		return JSON.parse(localStorage.getItem(sessionStorageKey) ?? "null") as {
			room?: string;
			key?: string;
			name?: string;
		} | null;
	} catch {
		return null;
	}
})();
const initialRoom = params.get("collab") ?? savedSession?.room ?? "";
const initialKey = params.get("key") ?? savedSession?.key ?? "";
const nicknameStorageKey = "amll-collaboration-nickname";

const normalizeRoom = (value: string) =>
	value
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9_-]/g, "-")
		.slice(0, 64);

export const Collaboration = () => {
	const [roomInput, setRoomInput] = useState(initialRoom);
	const [room, setRoom] = useState<string | null>(initialRoom || null);
	const [roomKey, setRoomKey] = useState(
		initialKey || (initialRoom ? makeRoomKey() : ""),
	);
	const [roomName, setRoomName] = useState(savedSession?.name ?? initialRoom);
	const [nickname, setNickname] = useState(
		() => localStorage.getItem(nicknameStorageKey) ?? "Editor",
	);
	const [visibility, setVisibility] = useState<"public" | "private">("public");
	const [publicRooms, setPublicRooms] = useState<CollaborationRoomListing[]>(
		[],
	);
	const [browserError, setBrowserError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const {
		status,
		peers,
		peerCount,
		error,
		isHost,
		closeRoom,
		renameRoom,
		serverRoomName,
	} = useCollaboration(room, roomKey || null, nickname);

	const refreshRooms = useCallback(async () => {
		try {
			setBrowserError(null);
			setPublicRooms(await listPublicRooms());
		} catch (refreshError) {
			setBrowserError(
				refreshError instanceof Error
					? refreshError.message
					: "Could not load rooms",
			);
		}
	}, []);

	useEffect(() => {
		if (!room) void refreshRooms();
	}, [room, refreshRooms]);
	useEffect(() => {
		if (serverRoomName) setRoomName(serverRoomName);
	}, [serverRoomName]);

	const setRoomUrl = useCallback((nextRoom: string, key: string, name?: string) => {
		const nextParams = new URLSearchParams({ collab: nextRoom, key });
		if (name) nextParams.set("name", name);
		window.history.replaceState(
			null,
			"",
			`${window.location.pathname}?${nextParams}`,
		);
		localStorage.setItem(
			sessionStorageKey,
			JSON.stringify({ room: nextRoom, key, name }),
		);
	}, []);
	useEffect(() => {
		if (room && serverRoomName) setRoomUrl(room, roomKey, serverRoomName);
	}, [room, roomKey, serverRoomName, setRoomUrl]);

	const leave = () => {
		setRoom(null);
		setRoomKey("");
		setRoomInput("");
		setRoomName("");
		localStorage.removeItem(sessionStorageKey);
		window.history.replaceState(null, "", window.location.pathname);
	};

	const join = () => {
		const normalized = normalizeRoom(roomInput);
		if (!normalized) return;
		const key = room === normalized && roomKey ? roomKey : makeRoomKey();
		setRoomInput(normalized);
		setRoom(normalized);
		setRoomKey(key);
		const name = roomName || normalized;
		setRoomName(name);
		setRoomUrl(normalized, key, name);
	};

	const create = async () => {
		setBusy(true);
		try {
			const created = await createRoom(
				roomName.trim() || "Untitled room",
				visibility,
			);
			setRoomInput(created.id);
			setRoom(created.id);
			setRoomKey(created.ownerKey);
			setRoomName(created.name);
			setRoomUrl(created.id, created.ownerKey, created.name);
		} catch (createError) {
			setBrowserError(
				createError instanceof Error
					? createError.message
					: "Could not create room",
			);
		} finally {
			setBusy(false);
		}
	};

	const joinPublic = (listing: CollaborationRoomListing) => {
		const key = makeRoomKey();
		setRoomInput(listing.id);
		setRoom(listing.id);
		setRoomKey(key);
		setRoomName(listing.name);
		setRoomUrl(listing.id, key, listing.name);
	};

	const saveNickname = () => {
		const next = nickname.trim() || "Editor";
		setNickname(next);
		localStorage.setItem(nicknameStorageKey, next);
	};

	const link = room
		? `${window.location.origin}${window.location.pathname}?collab=${encodeURIComponent(room)}&key=${encodeURIComponent(roomKey)}&name=${encodeURIComponent(roomName)}`
		: "";
	const connectionText =
		status === "connected"
			? `Connected · ${peerCount} other editor${peerCount === 1 ? "" : "s"}`
			: status;
	const currentRoomName = roomName || serverRoomName || room || "Untitled room";

	return (
		<Popover.Root>
			<Popover.Trigger>
				<Button
					variant={room ? "soft" : "ghost"}
					color={status === "error" ? "red" : "gray"}
					size="1"
				>
					{room ? `● ${peerCount + 1}` : "Collaborate"}
				</Button>
			</Popover.Trigger>
			<Popover.Content width="340px">
				<Flex direction="column" gap="2">
					{room ? (
						<>
							<Text size="2" weight="bold">
								{currentRoomName}
							</Text>
							<Text size="1" color="gray">
								Room ID: {room}
							</Text>
							<TextField.Root
								value={roomName}
								onChange={(event) => setRoomName(event.target.value)}
								placeholder="Room name"
								onKeyDown={(event) => {
									if (event.key === "Enter")
										renameRoom(roomName.trim() || room);
								}}
							/>
							<Flex gap="2">
								<TextField.Root
									style={{ flex: 1 }}
									value={nickname}
									onChange={(event) => setNickname(event.target.value)}
									placeholder="Nickname"
									onKeyDown={(event) => event.key === "Enter" && saveNickname()}
								/>
								<Button size="1" onClick={saveNickname}>
									Save name
								</Button>
							</Flex>
							<Flex gap="2" wrap="wrap">
								<Button
									variant="soft"
									size="1"
									onClick={() => navigator.clipboard.writeText(link)}
								>
									Copy invite link
								</Button>
								<Button
									size="1"
									onClick={async () => {
										try {
											renameRoom(roomName.trim() || room);
										} catch (renameError) {
											setBrowserError(
												renameError instanceof Error
													? renameError.message
													: "Only the room owner can rename this room",
											);
										}
									}}
								>
									Rename
								</Button>
								{isHost ? (
									<Button
										color="red"
										variant="soft"
										size="1"
										onClick={() => {
											if (window.confirm("Close this room for everyone?")) {
												closeRoom();
												leave();
											}
										}}
									>
										Close room
									</Button>
								) : (
									<Button color="red" variant="soft" size="1" onClick={leave}>
										Leave room
									</Button>
								)}
							</Flex>
							<Text
								size="1"
								color={
									status === "connected"
										? "green"
										: status === "error"
											? "red"
											: "gray"
								}
							>
								{connectionText}
								{error ? ` · ${error}` : ""}
							</Text>
							{peers.length > 0 && (
								<Flex direction="column" gap="1">
									{peers.map((peer) => (
										<Text key={peer.clientId} size="1" color="gray">
											● {peer.name || "Editor"} · {peer.mode || "edit"} · v
											{peer.version || "unknown"}
										</Text>
									))}
								</Flex>
							)}
						</>
					) : (
						<>
							<Text size="2" weight="bold">
								Collaboration rooms
							</Text>
							<Flex gap="2">
								<TextField.Root
									style={{ flex: 1 }}
									value={nickname}
									onChange={(event) => setNickname(event.target.value)}
									placeholder="Nickname"
									onKeyDown={(event) => event.key === "Enter" && saveNickname()}
								/>
								<Button size="1" onClick={saveNickname}>
									Save name
								</Button>
							</Flex>
							<Flex gap="2">
								<TextField.Root
									style={{ flex: 1 }}
									value={roomName}
									onChange={(event) => setRoomName(event.target.value)}
									placeholder="New room name"
								/>
								<Button size="1" onClick={create} disabled={busy}>
									Create
								</Button>
							</Flex>
							<Flex gap="1">
								<Button
									size="1"
									variant={visibility === "public" ? "solid" : "soft"}
									onClick={() => setVisibility("public")}
								>
									Public
								</Button>
								<Button
									size="1"
									variant={visibility === "private" ? "solid" : "soft"}
									onClick={() => setVisibility("private")}
								>
									Private
								</Button>
							</Flex>
							<Text size="1" color="gray">
								Public rooms appear below. Private rooms are invite-only.
							</Text>
							<Flex gap="2">
								<TextField.Root
									style={{ flex: 1 }}
									value={roomInput}
									onChange={(event) => setRoomInput(event.target.value)}
									placeholder="Private room ID"
									onKeyDown={(event) => event.key === "Enter" && join()}
								/>
								<Button onClick={join} size="1">
									Join
								</Button>
							</Flex>
							<Flex justify="between" align="center">
								<Text size="1" weight="bold">
									Public rooms
								</Text>
								<Button
									size="1"
									variant="ghost"
									onClick={() => void refreshRooms()}
								>
									Refresh
								</Button>
							</Flex>
							{publicRooms.length === 0 ? (
								<Text size="1" color="gray">
									No public rooms yet.
								</Text>
							) : (
								publicRooms.map((listing) => (
									<Flex
										key={listing.id}
										justify="between"
										align="center"
										gap="2"
									>
										<Text size="1" truncate>
											{listing.name}{" "}
											<Text as="span" color="gray">
												({listing.id}) · {listing.users ?? 0} user
												{listing.users === 1 ? "" : "s"}
											</Text>
										</Text>
										<Button
											size="1"
											variant="soft"
											onClick={() => joinPublic(listing)}
										>
											Join
										</Button>
									</Flex>
								))
							)}
						</>
					)}
					{browserError && (
						<Text size="1" color="red">
							{browserError}
						</Text>
					)}
				</Flex>
			</Popover.Content>
		</Popover.Root>
	);
};

export default Collaboration;
